import type { SearchResult, SourceListItem } from "#/types";

import {
  addToLibrary as apiAddToLibrary,
  search as apiSearch,
  getSources,
  startDownload,
} from "#/client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function useSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [sources, setSources] = useState<SourceListItem[]>([]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [failedSources, setFailedSources] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState("");
  const [addingUrl, setAddingUrl] = useState<string | undefined>(undefined);

  const fetchSources = async () => {
    try {
      const data = await getSources();
      // Map SourceInfo[] to SourceListItem[]
      const sources: SourceListItem[] = data
        .filter((s) => !s.disabled)
        .map((s) => ({
          meta: s.config.meta,
        }));
      setSources(sources);
    } catch (error) {
      console.error("Failed to fetch sources:", error);
    }
  };

  useEffect(() => {
    void fetchSources();
  }, []);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!query) {
      return;
    }

    setSearching(true);
    setHasSearched(true);
    setSearchedQuery(query);
    setResults([]);
    setFailedSources([]);
    try {
      const data = await apiSearch(query, source);
      setResults(data.results);
      setFailedSources(data.failed_sources);
    } catch (error) {
      console.error("Search failed:", error);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addToLibrary = async (result: SearchResult) => {
    setAddingUrl(result.url);
    try {
      const book = await apiAddToLibrary(result.url, result.source_id);

      // Update local state to show it's in library
      setResults((prev) =>
        prev.map((r) => (r.url === result.url ? { ...r, in_library: true } : r)),
      );

      try {
        await startDownload(book.id, book.needs_extension, 5);
      } catch (error) {
        console.error("Failed to auto-start download:", error);
      }
    } catch (error) {
      console.error("Failed to add to library:", error);
      alert(`Failed to add: ${String(error)}`);
    } finally {
      setAddingUrl(undefined);
    }
  };

  return {
    query,
    source,
    sources,
    results,
    failedSources,
    searching,
    hasSearched,
    searchedQuery,
    addingUrl,
    setQuery,
    setSource,
    handleSearch,
    addToLibrary,
  };
}
